// src/utils/utils.js
// *******************************************************************************
// UTILS (ESM)
// *******************************************************************************

class Utils {
  // Return initials: if input has 1–2 non-space chars, use them as-is; else first of first word + first of last word.
  static getNameInitial(name) {
    if (name == null) return null;
    const trimmed = String(name).trim();
    if (!trimmed) return null;
    const compact = trimmed.replace(/\s+/g, '');
    if (compact.length === 1) return compact.toUpperCase();
    if (compact.length === 2) return compact.toUpperCase();

    // single word: take first two letters
    if (!/\s/.test(trimmed)) {
      return trimmed.slice(0, 2).toUpperCase();
    }

    // default: first char of first & last words
    const initials = trimmed.match(/\b\w/g) || [];
    return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
  }

  // Find route info in navigation tree based on path
  static getRouteInfo(navTree, path) {
    if (!navTree) return;
    if (path.includes(navTree.path) && (!navTree?.submenu || navTree.submenu.length === 0)) {
      return navTree;
    }
    let route;
    for (let p in navTree) {
      if (navTree.hasOwnProperty(p) && typeof navTree[p] === 'object') {
        route = this.getRouteInfo(navTree[p], path);
        if (route) return route;
      }
    }
    return route;
  }

  // Accessible color contrast from hex
  static getColorContrast(hex) {
    if (!hex) return 'dark';
    const threshold = 130;
    const hRed = hexToR(hex);
    const hGreen = hexToG(hex);
    const hBlue = hexToB(hex);

    function hexToR(h) {
      return parseInt(cutHex(h).substring(0, 2), 16);
    }
    function hexToG(h) {
      return parseInt(cutHex(h).substring(2, 4), 16);
    }
    function hexToB(h) {
      return parseInt(cutHex(h).substring(4, 6), 16);
    }
    function cutHex(h) {
      return h.charAt(0) === '#' ? h.substring(1, 7) : h;
    }

    const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    return cBrightness > threshold ? 'dark' : 'light';
  }

  // Darken or lighten a hex color
  static shadeColor(color, percent) {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);
    R = parseInt((R * (100 + percent)) / 100);
    G = parseInt((G * (100 + percent)) / 100);
    B = parseInt((B * (100 + percent)) / 100);
    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;
    const RR = R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
    const GG = G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
    const BB = B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);
    return `#${RR}${GG}${BB}`;
  }

  // Convert RGBA to HEX
  static rgbaToHex(rgba) {
    const trim = str => str.replace(/^\s+|\s+$/gm, '');
    const inParts = rgba.substring(rgba.indexOf('(')).split(',');
    const r = parseInt(trim(inParts[0].substring(1)), 10);
    const g = parseInt(trim(inParts[1]), 10);
    const b = parseInt(trim(inParts[2]), 10);
    const a = parseFloat(trim(inParts[3].substring(0, inParts[3].length - 1))).toFixed(2);
    const outParts = [
      r.toString(16),
      g.toString(16),
      b.toString(16),
      Math.round(a * 255)
        .toString(16)
        .substring(0, 2),
    ];
    outParts.forEach((part, i) => {
      if (part.length === 1) outParts[i] = '0' + part;
    });
    return `#${outParts.join('')}`;
  }

  // Return positive/negative mapped value
  static getSignNum(number, positive, negative) {
    if (number > 0) return positive;
    if (number < 0) return negative;
    return null;
  }

  // antd Table sorter helper
  static antdTableSorter(a, b, key) {
    if (typeof a[key] === 'number' && typeof b[key] === 'number') {
      return a[key] - b[key];
    }
    if (typeof a[key] === 'string' && typeof b[key] === 'string') {
      const aa = a[key].toLowerCase();
      const bb = b[key].toLowerCase();
      return aa > bb ? -1 : bb > aa ? 1 : 0;
    }
    return;
  }

  // Filter array of objects by key===value
  static filterArray(list, key, value) {
    let data = list;
    if (list) data = list.filter(item => item[key] === value);
    return data;
  }

  // Remove object from array by key!==value
  static deleteArrayRow(list, key, value) {
    let data = list;
    if (list) data = list.filter(item => item[key] !== value);
    return data;
  }

  // Wildcard search on all properties
  static wildCardSearch(list, input) {
    const searchText = item => {
      for (let key in item) {
        if (item[key] == null) continue;
        if (item[key].toString().toUpperCase().indexOf(input.toString().toUpperCase()) !== -1) {
          return true;
        }
      }
    };
    return list.filter(value => searchText(value));
  }

  // Collect active breakpoints from antd Grid.useBreakpoint()
  static getBreakPoint(screens) {
    const breakpoints = [];
    for (const key in screens) {
      if (screens.hasOwnProperty(key)) {
        const el = screens[key];
        if (el) breakpoints.push(key);
      }
    }
    return breakpoints;
  }
}

export default Utils;
